import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { Popover, PopoverModule } from 'primeng/popover';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { CrudService } from 'src/core/services/crud/crud.service';
import { IvrInstanceDto } from '../../models/ivr-instance.dto';
import { IvrTenantLookupDto } from '../../models/ivr-tenant-lookup.dto';

type IvrInstanceTplContext = BaseListColumnTemplateContext<IvrInstanceDto>;

@Component({
  selector: 'app-ivr-instance-list',
  standalone: true,
  imports: [CommonModule, LocalizationPipe, PopoverModule, ActionMenuComponent, BaseListComponent],
  templateUrl: './ivr-instance-list.component.html',
  styleUrls: ['./ivr-instance-list.component.scss'],
})
export class IvrInstanceListComponent implements OnInit, AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly crudService = inject(CrudService);

  @ViewChild('tenantPopover') tenantPopoverRef!: Popover;
  @ViewChild('nameTpl') nameTpl!: TemplateRef<IvrInstanceTplContext>;
  @ViewChild('tenantsTpl') tenantsTpl!: TemplateRef<IvrInstanceTplContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<IvrInstanceTplContext>;

  @Input() instances: IvrInstanceDto[] = [];
  @Input() loading = false;

  @Output() edit = new EventEmitter<IvrInstanceDto>();
  @Output() delete = new EventEmitter<IvrInstanceDto>();

  listConfig!: BaseListConfig<IvrInstanceDto>;
  tenantPopoverInstance: IvrInstanceDto | null = null;

  private readonly tenantNameById = new Map<string, string>();

  ngOnInit(): void {
    this.crudService
      .getSimple<IvrTenantLookupDto[]>(IvrInstanceDto, null, 'tenant-lookup')
      .subscribe({
        next: list => {
          this.tenantNameById.clear();
          (list ?? []).forEach(t => this.tenantNameById.set(t.id, t.name));
        },
      });
  }

  get popoverTenantNames(): string[] {
    const assignedTenants = this.tenantPopoverInstance?.assignedTenants;
    if (!Array.isArray(assignedTenants)) {
      return [];
    }
    return assignedTenants.map(t => this.tenantNameById.get(t.tenantId) ?? t.tenantId);
  }

  ngAfterViewInit(): void {
    const l = (key: string) => this.localization.instant(key);

    const columns: BaseListColumn<IvrInstanceDto>[] = [
      { field: 'name', header: l('FreeswitchIntegration::InstanceName'), bodyTemplate: this.nameTpl },
      { field: 'ipAddress', header: l('FreeswitchIntegration::IpAddress') },
      { field: 'port', header: l('FreeswitchIntegration::Port') },
      {
        field: 'assignedTenants',
        header: l('FreeswitchIntegration::AssignedTenants'),
        bodyTemplate: this.tenantsTpl,
      },
      { field: 'id', header: l('FreeswitchIntegration::Actions'), width: '140px', bodyTemplate: this.actionsTpl },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
      emptyMessage: l('FreeswitchIntegration::NoIvrInstancesFound'),
    };

    this.cdr.detectChanges();
  }

  getMenuItems(row: IvrInstanceDto): ActionMenuItem[] {
    return [
      {
        label: this.localization.instant('FreeswitchIntegration::EditIvrInstance'),
        icon: 'pi pi-pencil',
        action: () => this.edit.emit(row),
      },
      {
        label: this.localization.instant('FreeswitchIntegration::Delete'),
        icon: 'pi pi-trash',
        severity: 'danger',
        action: () => this.delete.emit(row),
      },
    ];
  }

  trackByIndex(index: number): number {
    return index;
  }

  showTenantPopover(instance: IvrInstanceDto, event: Event): void {
    event.stopPropagation();
    this.tenantPopoverInstance = instance;
    this.tenantPopoverRef.toggle(event);
  }
}
